/** @jsx jsx */
import { css, jsx } from "@emotion/core"

import { Button, IconButton, isBlank, Spacer } from "@cd2/components"
import { Grid } from "@cd2/components/build/es6/Grid"
import { Fields, Form2 } from "@cd2/forms"
import { FormState } from "@cd2/forms/build/es6/Form2/FormState"
import { H4, P } from "@cd2/text/build/es6"
import { genId } from "@cd2/tools"
import React, { useEffect, useRef, useState } from "react"
import { ButtonsWrapper } from "../../Components/ButtonsWrapper"
import { brandColors, colors } from "../../Components/EOSThemeProvider"
import { FieldGroup } from "../../Components/FieldGroup"
import { DpdProductSelectList } from "../../Components/FormFields/DpdProductSelectList"
import { useGlobalState } from "../../Components/GlobalStateProvider"
import { KeyValueList } from "../../Components/KeyValueList"
import { FloatingBox } from "../../Components/Misc/FloatingBox"
import { QuickToolTip } from "../../Components/QuickTooltip"
import { icons } from "../../constants/icons"
import { useApi } from "../../hooks/useApi"
import { useAsyncEffect } from "../../hooks/useAsyncEffect"
import { useConfirmation } from "../../hooks/useConfirmation"
import {
  ConfirmAlternateProductModal,
  useProductAlternative,
} from "../../hooks/useProductAlternative"
import { useSiteAlerts } from "../../hooks/useSiteAlerts"
import { ProductSelect } from "../../stories/quoteOrders/ItemRowSageProduct"
import { IValidationResponse } from "../../types/common"
import { IRecord } from "../../types/schema"
import { spliceArray } from "../../utils/arrays"
import type { IProductStock } from "../../../server/productLocations/aggregateProductStock"
import { formStateSetErrors } from "../../utils/formStateSetErrors"
import { formatPrice } from "../../utils/utils"
import {
  BoxQuantitySwitch,
  handleLoadOrderRequestProductData,
  OrderRequestAddressFormFields,
  SourceOrderSelect,
} from "./OrderRequestsStandaloneForm"
import { StockAvailableInOtherBranchesPanel } from "../../Components/PurchaseOrdersForm2"

interface IBranch {
  id: string
  name: string
}

export interface IOrderRequest {
  dpdProduct?: IRecord<"products", "body" | "sale_price_pennies">
  created_by_branch?: { id: string; name: string }
  received_by_branch?: { id: string; name: string }
  ship_to_branch?: boolean
  part_ship?: boolean
  notes?: string
  items?: IItem[]
  shipping_address_name?: string
  shipping_address_line_1?: string
  shipping_address_line_2?: string
  shipping_address_line_3?: string
  shipping_address_line_4?: string
  shipping_address_city?: string
  shipping_address_county?: string
  shipping_address_country?: string
  shipping_address_postcode?: string
  shipping_address_phone?: string
  shipping_address_email?: string
  billing_address_name?: string
  billing_address_line_1?: string
  billing_address_line_2?: string
  billing_address_line_3?: string
  billing_address_line_4?: string
  billing_address_city?: string
  billing_address_county?: string
  billing_address_country?: string
  billing_address_postcode?: string
  billing_address_phone?: string
  billing_address_email?: string
  sourceOrder?: IRecord<"orders">
  shunt?: boolean
}

interface IProps {
  xRef?: React.MutableRefObject<FormState<IOrderRequest> | null>
  initialData?: IOrderRequest
  onSubmit?: (data: IOrderRequest, actionType?: "save" | "saveAndPreview") => any
  onError?: () => void
  onDataChange?: (data: IOrderRequest) => void
  errors?: IOrderRequestErrors
  isBuildRequired: (productId: string) => boolean
  toggleBuildRequired: (productId: string) => void
}

export const ORDER_REQUEST_ITEM_NEW_ID_PREFIX = "new"

export function OrderRequestForm({
  xRef,
  initialData = {},
  onSubmit,
  onError,
  errors,
  onDataChange,
  isBuildRequired,
  toggleBuildRequired,
}: IProps) {
  const actionRef = React.useRef<"save" | "saveAndPreview">()
  const alerts = useSiteAlerts()
  const { currentUser } = useGlobalState()

  const formRef = useRef<FormState<IOrderRequest>>(null)
  if (xRef) {
    xRef.current = formRef.current
  }

  useEffect(() => {
    if (!formRef.current) {
      return
    }
    formStateSetErrors(formRef.current, errors || {})
  }, [formRef.current, JSON.stringify(errors)])

  function handleSubmit(data: IOrderRequest, formState: FormState<IOrderRequest>) {
    const { isValid, errors } = validateForm(formState.getData())
    if (isValid) {
      formStateSetErrors(formState, {})
      onSubmit?.(data, actionRef.current)
    } else {
      console.warn("Validations failed for order request form", errors)

      if (errors.created_by_branch) {
        alerts.genericError("Please select a source branch")
      } else if (errors.received_by_branch) {
        alerts.genericError("Please select a target branch")
      } else if (errors.items_length) {
        alerts.genericError("Please add at least one item")
      } else if (Object.values(errors.items ?? {}).some(item => item.target_product)) {
        alerts.genericError(
          "Some of the items are not available at the target branch - please check the items",
        )
      }
      formStateSetErrors(formState, errors)
    }
  }

  return (
    <div
      css={css`
        max-width: 900px;
      `}
    >
      <Form2.Form
        xRef={formRef}
        initialData={initialData}
        onSubmit={handleSubmit}
        onError={onError}
        onChange={onDataChange}
      >
        <FloatingBox
          header={<H4>1. Order request</H4>}
          css={css`
            max-width: 500px;
          `}
        >
          <Grid gap={8}>
            <Form2.TextArea
              field="notes"
              placeholder="Leave any notes for the receiving branch..."
            />
            {currentUser.canShuntOrderRequests && (
              <Form2.Checkbox field="shunt" label="Mark this order request as SHUNT" />
            )}
          </Grid>
        </FloatingBox>
        <Form2.WithForm
          render={form => {
            const targetBranch = form.getField("received_by_branch").getValue()
            const sourceBranch = form.getField("created_by_branch").getValue()
            const shipToBranchField = form.getField("ship_to_branch")
            const partShipField = form.getField("part_ship")
            if (!targetBranch || !sourceBranch) {
              return null
            }
            return (
              <React.Fragment>
                <OrderRequestItemsForm
                  targetBranch={targetBranch}
                  sourceBranch={sourceBranch}
                  isBuildRequired={isBuildRequired}
                  toggleBuildRequired={toggleBuildRequired}
                />
                <FloatingBox header={<H4>3. Addresses</H4>}>
                  <Grid gap={16} columns={[{ fr: 1 }, { fr: 1 }]}>
                    <FieldGroup name="Shipping Address">
                      <Fields.Checkbox
                        label="Ship this item to the branch"
                        onChange={value => {
                          shipToBranchField.setValue(value)
                          partShipField.setValue(value)
                        }}
                        value={shipToBranchField.getValue()}
                      />
                      <Form2.WithField
                        field="ship_to_branch"
                        render={field =>
                          !field.getValue() ? (
                            <React.Fragment>
                              <Spacer />
                              <OrderRequestAddressFormFields type="shipping_address" />
                            </React.Fragment>
                          ) : (
                            <React.Fragment />
                          )
                        }
                      />
                    </FieldGroup>
                    <FieldGroup name="Billing Address">
                      <OrderRequestAddressFormFields type="billing_address" />
                    </FieldGroup>
                  </Grid>
                </FloatingBox>
                <FloatingBox header={<H4>4. Delivery</H4>}>
                  <Form2.WithField<IOrderRequest["dpdProduct"]>
                    field="dpdProduct"
                    render={field => (
                      <DpdProductSelectList
                        label={
                          <Form2.FieldLabel
                            label={<span>Delivery Service</span>}
                            fieldName={`dpdProduct`}
                            required
                          />
                        }
                        filter={{ not: { matches: { body: { like: "direct del" } } } }}
                        branchId={targetBranch.id}
                        onChange={dpdProduct => {
                          field.setValue(dpdProduct || undefined)
                        }}
                        value={field.getValue()}
                        error={field.getError()}
                        orderRequestFilter
                      />
                    )}
                  />
                </FloatingBox>
              </React.Fragment>
            )
          }}
          subscribe={{ dataChange: true }}
        />
        <SourceOrderSelect />

        {onSubmit && (
          <ButtonsWrapper>
            <Button
              onClick={() => {
                actionRef.current = "saveAndPreview"
              }}
              buttonProps={{ type: "submit" }}
            >
              Save and preview
            </Button>
          </ButtonsWrapper>
        )}
      </Form2.Form>
    </div>
  )
}

export interface IOrderRequestErrors {
  created_by_branch?: string
  received_by_branch?: string
  items_length?: string
  billing_address_name?: string
  billing_address_line_1?: string
  billing_address_postcode?: string
  shipping_address_name?: string
  shipping_address_line_1?: string
  shipping_address_postcode?: string
  items?: Record<
    string,
    {
      quantity?: string
      price_pennies?: string
      target_product?: string
      discountPercent?: string
    }
  >
  dpdProduct?: string
}

export { validateForm as validateOrderRequestForm }
export const orderRequestErrorsMap: Partial<Record<keyof IOrderRequestErrors, string>> = {
  dpdProduct: "Delivery service",
}

function validateForm(data: IOrderRequest): IValidationResponse<IOrderRequestErrors> {
  let isValid = true
  const errors: IOrderRequestErrors = {}

  if (!data.dpdProduct) {
    isValid = false
    errors.dpdProduct = "is required"
  }

  if (!data.created_by_branch) {
    isValid = false
    errors.created_by_branch = "is required"
  }
  if (!data.received_by_branch) {
    isValid = false
    errors.received_by_branch = "is required"
  }
  if (!data.items || data.items.length === 0) {
    isValid = false
    errors.items_length = "You must have at least 1 item"
  }

  const addressTypes = ["shipping_address", "billing_address"] as const
  for (const addressType of addressTypes) {
    if (addressType === "shipping_address" && data.ship_to_branch) {
      continue
    }

    if (isBlank(data[`${addressType}_name`])) {
      isValid = false
      errors[`${addressType}_name`] = "is required"
    }
    if (isBlank(data[`${addressType}_line_1`])) {
      isValid = false
      errors[`${addressType}_line_1`] = "is required"
    }
    if (
      isBlank(data[`${addressType}_postcode`]) &&
      data[`${addressType}_country`] === "United Kingdom"
    ) {
      isValid = false
      errors[`${addressType}_postcode`] = "is required"
    }
  }

  for (const item of data.items ?? []) {
    const itemErrors: any = {}
    if (item.body) {
      continue
    }
    if (!item.quantity || item.quantity < 1) {
      isValid = false
      itemErrors.quantity = "Quantity must be greater than 0"
    }

    if (!item.price_pennies) {
      isValid = false
      itemErrors.price_pennies = "Price must be greater than 0"
    }

    if (Object.keys(itemErrors).length > 0) {
      errors.items = errors.items ?? {}
      errors.items[item.id] = itemErrors
    }
  }

  return { isValid, errors }
}

interface IItem {
  id: string
  source_product?: { id: string } | null
  target_product?: { id: string; stock_code: string } | null
  quantity?: number | null
  source_cost_price_pennies?: number | null
  target_cost_price_pennies?: number | null
  price_pennies?: number | null
  discountPercent?: number | null
  stock_code?: string
  body?: string
  buildRequired?: boolean
  type: "product"
  alternative?: boolean
}

interface IOrderRequestItemsFormProps {
  targetBranch: IBranch
  sourceBranch: IBranch
  isBuildRequired: (productId: string) => boolean
  toggleBuildRequired: (productId: string) => void
}

function OrderRequestItemsForm({
  targetBranch,
  sourceBranch,
  isBuildRequired,
  toggleBuildRequired,
}: IOrderRequestItemsFormProps) {
  const itemsLengthError = Form2.useField("items_length", { subscribe: true }).getError()
  const itemsField = Form2.useField<IItem[]>("items", {
    subscribe: true,
    defaultValue: [],
  })

  // Add new item
  function handleAdd() {
    itemsField.setValue([
      ...itemsField.getValue(),
      { quantity: 1, id: `${ORDER_REQUEST_ITEM_NEW_ID_PREFIX}_${genId()}`, type: "product" },
    ])
  }

  // Remove item
  function handleRemove(index: number) {
    itemsField.setValue(itemsField.getValue().filter((_, i) => i !== index))
  }

  // Change item
  function handleChange(index: number, value: IItem) {
    itemsField.setValue(spliceArray(itemsField.getValue(), index, 1, value))
  }

  return (
    <FloatingBox header={<H4>2. Add products</H4>}>
      {itemsLengthError && (
        <P
          color="red"
          css={css`
            margin-bottom: 16px;
          `}
        >
          {itemsLengthError}
        </P>
      )}
      <Grid gap={4}>
        {itemsField.getValue().length > 0 && (
          <Grid columns={[{ fr: 1 }, 100]} gap={6} alignItems="start">
            <Grid
              columns={{ repeat: { times: "auto-fit", size: { min: 100, max: { fr: 1 } } } }}
              gap={8}
            >
              <Form2.FieldLabel label={<b>Product</b>} fieldName={`product`} required />
              <Form2.FieldLabel label={<b>Qty</b>} fieldName={`quantity`} required />
              <Form2.FieldLabel label={<b>Price</b>} fieldName={`price_pennies`} required />
            </Grid>
          </Grid>
        )}
        {itemsField.getValue().map((item, index) => (
          <Grid key={item.id} columns={[{ fr: 1 }, 100]} gap={6} alignItems="start">
            <div>
              <OrderRequestItemForm
                onChange={item => handleChange(index, item)}
                targetBranch={targetBranch}
                sourceBranch={sourceBranch}
                item={item}
                error={itemsField.getError()?.[item.id]}
                isBuildRequired={isBuildRequired}
                toggleBuildRequired={toggleBuildRequired}
              />
            </div>
            <IconButton
              onClick={() => handleRemove(index)}
              icon={icons.cross}
              buttonColor={brandColors.blaze}
              square
            />
          </Grid>
        ))}
      </Grid>
      <div
        css={css`
          text-align: right;
          margin-left: -16px;
          margin-right: -16px;
          padding-left: 16px;
          padding-right: 16px;

          ${itemsField.getValue().length > 0 &&
          css`
            margin-top: 16px;
            padding-top: 16px;
            border-top: 1px solid var(--floating-box-border-color);
          `}
        `}
      >
        <Button leftIcon={icons.plus} onClick={handleAdd} buttonColor="white">
          Add item
        </Button>
      </div>
    </FloatingBox>
  )
}

interface IProduct {
  id: string
  stock_code: string
  body: string
  branch: IBranch
  cost_price_pennies: number
  product_locations: { id: string }[]
}

function OrderRequestItemForm({
  targetBranch,
  sourceBranch,
  item,
  onChange,
  error,
  isBuildRequired,
  toggleBuildRequired,
}: {
  sourceBranch: IBranch
  targetBranch: IBranch
  item: IItem
  onChange(item: IItem): void
  error?: Exclude<IOrderRequestErrors["items"], undefined>[0]
  isBuildRequired: (productId: string) => boolean
  toggleBuildRequired: (productId: string) => void
}) {
  const skeem = window.skeem
  const [confirmAltProductNode, confirmAltProduct] = useConfirmation<any, { id: string }>(
    ({ onResolve, data }) => <ConfirmAlternateProductModal data={data} onResolve={onResolve} />,
  )
  const checkProductForAlternative = useProductAlternative()
  const [products, setProducts] = useState<IProduct[]>([])
  const [isFocused, setIsFocused] = useState(false)
  const targetBranchField = Form2.useField("received_by_branch", {
    subscribe: true,
  })

  async function handleLoadProductData(selectedProduct: { id: string }, skipAltCheck?: boolean) {
    let ogProduct = selectedProduct
    const alt = await checkProductForAlternative(ogProduct.id)
    if (alt.alternative && !skipAltCheck) {
      const selected = await confirmAltProduct(alt)
      const loaded = await skeem.fetch2<IRecord<"products">>({
        first: {
          name: "products",

          filter: { matches: { id: selected.id } },
        },
      })
      ogProduct = loaded
    }

    const { product, sourceBranchData, targetBranchData, alternative, targetBranchPrice } =
      await handleLoadOrderRequestProductData({
        sourceBranchId: sourceBranch.id,
        targetBranchId: targetBranch.id,
        productId: ogProduct.id,
      })

    setProducts([sourceBranchData, targetBranchData].filter(Boolean))
    onChange({
      ...item,
      stock_code: product.stock_code,
      body: product.body,
      target_cost_price_pennies: targetBranchData?.cost_price_pennies,
      source_cost_price_pennies: sourceBranchData?.cost_price_pennies,
      target_product: targetBranchData,
      source_product: sourceBranchData,
      price_pennies: targetBranchPrice,
      alternative,
      type: "product",
    })
  }

  useAsyncEffect(async () => {
    if (item.source_product && item.stock_code && item.body) {
      await handleLoadProductData({ id: item.source_product.id }, true)
    }
  }, [targetBranch.id, item.stock_code])

  const hasStockError = (
    (targetBranchField.getError() ?? []).notEnoughStockForItems ?? []
  ).includes(item.id)

  return (
    <div
      onFocus={() => setIsFocused(true)}
      onBlur={() => setIsFocused(false)}
      css={css`
        padding: ${isFocused ? 8 : 2}px 8px;
        margin: 0 -8px;
        transition: 0.2s;
        background-color: ${isFocused ? "#f5f5f5" : "transparent"};
      `}
    >
      {confirmAltProductNode}
      <Grid gap={8}>
        <Grid
          columns={{ repeat: { times: "auto-fit", size: { min: 100, max: { fr: 1 } } } }}
          gap={8}
        >
          <div>
            <ProductSelect
              value={item.source_product?.id}
              onChange={handleLoadProductData}
              error={error?.target_product}
            />
            {item.alternative && (
              <React.Fragment>
                <Spacer size={4} />
                <QuickToolTip
                  content={
                    <P color="white">
                      This product has a flagged alternative item. When this order request is saved,
                      it will automatically be swapped for {item.target_product?.stock_code}
                    </P>
                  }
                >
                  <span
                    css={css`
                      color: grey;
                      border-bottom: 1px dotted grey;
                    `}
                  >
                    This item will be swapped for an alternative when requested
                  </span>
                </QuickToolTip>
              </React.Fragment>
            )}
            <BoxQuantitySwitch
              productId={item.source_product?.id}
              itemQuantity={item.quantity}
              onChange={qty => onChange({ ...item, quantity: qty })}
            />

            {item.source_product?.id && (
              <React.Fragment>
                <Spacer />
                <Fields.Checkbox
                  label="Build req"
                  onChange={() => toggleBuildRequired(item.source_product!.id)}
                  value={item.source_product && isBuildRequired(item.source_product.id)}
                />
              </React.Fragment>
            )}
          </div>
          <Fields.Number2
            value={item.quantity}
            onChange={value => onChange({ ...item, quantity: value })}
            placeholder="Stock 000"
            error={error?.quantity ?? (hasStockError ? "Not enough stock at branch" : undefined)}
          />
          <Fields.Price
            value={item.price_pennies ?? null}
            onChange={value => onChange({ ...item, price_pennies: value })}
            error={error?.price_pennies}
            inPennies
          />
          <Fields.Number2
            value={item.discountPercent ?? null}
            onChange={value => onChange({ ...item, discountPercent: value })}
            error={error?.discountPercent}
          />
        </Grid>
        {isFocused && (
          <StockAvailableInOtherBranchesPanel product={item.target_product || undefined} />
        )}
        {isFocused && products && products.length > 0 && (
          <Grid columns={[{ fr: 1 }, { fr: 1 }]} gap={8}>
            <ProductBranchInfo branch={sourceBranch} products={products} item={item} />
            <ProductBranchInfo branch={targetBranch} products={products} item={item} />
          </Grid>
        )}
      </Grid>
    </div>
  )
}

function ProductBranchInfo({
  products,
  branch,
  item,
}: {
  products: IProduct[]
  branch: IBranch
  item: IItem
}) {
  const { currency } = useGlobalState()
  const api = useApi()
  const [stockData, setStockData] = useState<IProductStock>()
  const alerts = useSiteAlerts()
  const product = products.find(product => product.branch.id === branch.id)

  useAsyncEffect(async () => {
    if (product) {
      await fetchStockDataForBranch(product.id)
    }
  }, [product])

  async function fetchStockDataForBranch(productId?: string) {
    if (!productId) {
      return
    }
    const data = await api.productsStock([productId])
    setStockData(data[productId])
  }

  Form2.useFormSubmitLock(cb => {
    const targetBranchField = cb.getField("received_by_branch")
    const error = targetBranchField.getError() ?? {}
    const newError = {
      ...error,
      notEnoughStockForItems: (error.notEnoughStockForItems ?? []).filter(
        (itemId: string) => itemId !== item.id,
      ),
    }
    targetBranchField.setError(newError)
    const targetBranch = cb.getField("received_by_branch").getValue()
    const qty = item.quantity ?? 0
    const inStock = stockData?.inStock ?? 0
    if (targetBranch.id === branch.id && qty > inStock) {
      alerts.genericError(
        `You've selected a higher quantity than is available in stock for ${product?.stock_code}`,
      )
      targetBranchField.setError({
        ...error,
        notEnoughStockForItems: [...(error.notEnoughStockForItems ?? []), item.id],
      })
      return false
    } else {
      return true
    }
  })

  return (
    <FloatingBox
      header={
        <P>
          <b>{branch.name}</b>
        </P>
      }
      color={product ? colors.infoNotice : colors.errorNotice}
    >
      {product ? (
        <KeyValueList
          labelWidth={100}
          data={[
            { label: "Stock code", value: product.stock_code },
            { label: "Description", value: product.body },
            {
              label: "Cost price",
              value: formatPrice(product.cost_price_pennies, { isPennies: true, currency }),
            },
            { label: "In stock", value: stockData?.inStock },
            { label: "On order", value: stockData?.onOrderStock },
          ]}
          inline
        />
      ) : (
        <P>
          There is no matching product at this branch. This may be due to a difference in stock
          code. Please remove this item to save the order request.
        </P>
      )}
    </FloatingBox>
  )
}
